import { createDisposer, logVerbose, type Unsubscribe } from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { getAuth } from '@/auth/authRef'
import type { Player } from '@/playback/api'
import type { PlaylistController, PlaylistEntry } from '@/playback/PlaylistController'
import { openPlaybackConcurrencyModal } from '@/playback/playbackConcurrencyModal'
import { trackRouteId } from '@/util/entityId'
import { reportDeviceStreaming, takeOverDeviceStreaming } from './deviceStreamingService'
import { DevicePlaybackConcurrencyState } from './concurrencyTypes'

const { target, errorHandler } = ctx('playbackConcurrencyBinding')

// Only music tracks and videos count against DMCS stream limits; podcasts,
// audiobooks and the like are exempt and report nothing. Mirrors hornet's
// `TYPES_REQUIRING_CONCURRENCY`.
const TYPES_REQUIRING_CONCURRENCY = new Set(['Track', 'Video'])

function requiresConcurrency(entry: PlaylistEntry): boolean {
    // Signed-out playback is a preview, and previews never take a stream slot.
    if (!getAuth().isSignedIn()) return false
    return entry.video === true || TYPES_REQUIRING_CONCURRENCY.has(entry.entity.__typename)
}

function contentIdOf(entry: PlaylistEntry): string | null {
    // DMCS resolves the report by bare asin. The raw GraphQL `entity.id` is a
    // catalog MRN for a signed-in track, which the resolver rejects ("Cannot
    // resolve object id"), so derive the asin the way detail-page links do.
    return trackRouteId(entry.entity)
}

export interface PlaybackConcurrencyBindingDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

/**
 * Reports local-device streaming to Firefly's Device Media Concurrency
 * Service (DMCS) at the start of each track/video and drives the
 * take-over prompt when the account is over its simultaneous-stream
 * limit. Observes the transport rather than gating it — it sits
 * alongside `initializePlaylistPlayerBindings` and only reaches in to
 * pause / resume when concurrency forces the issue.
 *
 * Flow mirrors hornet's `handleDevicePlaybackConcurrency`: report
 * PLAYING on start/resume and PAUSED / STOPPED on pause / stop so the
 * account's stream slot is released the moment the user pauses rather
 * than lingering until DMCS's own inactivity timeout (hornet reports
 * PAUSED from `LocalPlaybackController.pause` and STOPPED from
 * `PlaybackController.endPlaybackAndReleaseStream`). On
 * `MAX_CONCURRENCY_REACHED` pause and prompt; the user accepting takes
 * over the oldest stream and resumes; declining leaves playback paused.
 * A non-concurrency report failure degrades gracefully — it is logged
 * and playback continues (hornet couples its pause-on-error with
 * returning "allowed", so it never actually blocks the caller on a
 * network blip; requirement here is explicit: don't wedge playback).
 */
export function initializePlaybackConcurrencyBinding(
    deps: PlaybackConcurrencyBindingDeps
): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const { playerPromise, playlistController } = deps

    playerPromise
        .then((player) => {
            if (disposer.disposed) return

            // The open take-over prompt and the entry it is bound to. A step
            // to a different entry supersedes it — its continuation would take
            // over the OLD content — so it is dismissed (newest intent wins,
            // mirroring hornet's `dismissPlaybackConcurrencyModal` +
            // `pendingTakeOver` overwrite in `openPlaybackConcurrencyModal`).
            let concurrencyPrompt: { entry: PlaylistEntry; dismiss: () => void } | null = null

            // The slot we currently hold and its last reported state. A pause
            // releases it (PAUSED) and a resume of the SAME track re-acquires
            // it (PLAYING) — not the initial play, which the track-change
            // report below already covered — and a stop (queue emptied →
            // `current` goes null, or a step to concurrency-exempt content)
            // frees it (STOPPED). Mirrors hornet, which reports PAUSED from
            // `LocalPlaybackController.pause` and STOPPED from
            // `PlaybackController.endPlaybackAndReleaseStream`; a natural
            // track→track advance re-reports PLAYING (not STOPPED), so the
            // slot is only released on the transition to no-concurrency
            // content, never between two music entries.
            let held: {
                entry: PlaylistEntry
                contentId: string
                state: DevicePlaybackConcurrencyState
                // Whether this slot has produced audio yet. Guards PAUSED against a
                // pre-playback autoplay-policy bounce (see paused$ handler).
                playbackStarted: boolean
            } | null = null

            const takeOverAndResume = (entry: PlaylistEntry, contentId: string) => {
                takeOverDeviceStreaming(
                    contentId,
                    player.contentTime$.value * 1000,
                    DevicePlaybackConcurrencyState.PLAYING
                )
                    .then(() => {
                        if (disposer.disposed || playlistController.current.value !== entry) return
                        held = {
                            entry,
                            contentId,
                            state: DevicePlaybackConcurrencyState.PLAYING,
                            playbackStarted: false,
                        }
                        logVerbose(
                            target,
                            `[preload-trace] concurrency-reacquire: entry=${entry.id} contentId=${contentId}`
                        )
                        player.play().catch(errorHandler)
                    })
                    // Take-over failed: the slot was never granted, so leave
                    // playback paused rather than resume into a stream DMCS
                    // will kill.
                    .catch(errorHandler)
            }

            // Reports PLAYING for `entry` and, when DMCS denies the slot because
            // another device is at the account's stream limit, pauses and
            // prompts for take-over. Runs on both the initial play and a resume
            // of a paused track: hornet re-checks concurrency on every
            // transition to PLAYING through `_confirmConcurrencyOptimistic`
            // (`LocalPlaybackController.play` / `_updateControllerPlayingEntity`
            // / `_loadAndPlayCurrentEntity`), so a device that claimed the slot
            // while we were paused must block the resume, not just the first play.
            const reportPlayingAndGate = (entry: PlaylistEntry, contentId: string) => {
                reportDeviceStreaming(
                    contentId,
                    player.contentTime$.value * 1000,
                    DevicePlaybackConcurrencyState.PLAYING
                )
                    .then((result) => {
                        if (disposer.disposed || playlistController.current.value !== entry) return
                        if (result !== 'MAX_CONCURRENCY_REACHED') return
                        // Denied a slot: forget it so the pause() below doesn't
                        // emit a PAUSED release for a slot we never acquired.
                        held = null
                        // Pause now; the prompt decides whether the user
                        // reclaims a slot on this device.
                        player.pause()
                        concurrencyPrompt = {
                            entry,
                            dismiss: openPlaybackConcurrencyModal({
                                onResume: () => takeOverAndResume(entry, contentId),
                            }),
                        }
                    })
                    // Non-concurrency failure (network / gateway): don't wedge
                    // playback — log and let it continue.
                    .catch(errorHandler)
            }

            add(
                playlistController.current.onData((entry) => {
                    // A step to a different entry supersedes an open prompt —
                    // its take-over is bound to the old content.
                    if (concurrencyPrompt && concurrencyPrompt.entry !== entry) {
                        concurrencyPrompt.dismiss()
                        concurrencyPrompt = null
                    }
                    const contentId =
                        entry && requiresConcurrency(entry) ? contentIdOf(entry) : null
                    if (!entry || contentId == null) {
                        // Stopped, stepped to exempt content, or an entry with
                        // no resolvable asin: free the slot now instead of
                        // holding it until DMCS's inactivity timeout. Sign-out
                        // tears down the playlist through this same path
                        // (`clear()` nulls `current`); auth is already gone by
                        // then, so skip the report rather than firing an authed
                        // call that can only 401.
                        if (held && getAuth().isSignedIn()) {
                            reportDeviceStreaming(
                                held.contentId,
                                player.contentTime$.value * 1000,
                                DevicePlaybackConcurrencyState.STOPPED
                            ).catch(errorHandler)
                        }
                        held = null
                        return
                    }
                    // GH confirms an eager start's slot once its queue answers, with the
                    // queue's entry: reporting now would race that confirmation.
                    if (entry.eagerStartId) {
                        held = null
                        return
                    }
                    // A paused page-reload restore parks this entry without
                    // playing — don't grab a stream slot yet (that would kick
                    // the user's active device). Mark held PAUSED so the first
                    // resume acquires the slot via the playing$ handler below.
                    if (playlistController.restoringCurrent) {
                        held = {
                            entry,
                            contentId,
                            state: DevicePlaybackConcurrencyState.PAUSED,
                            playbackStarted: false,
                        }
                        return
                    }
                    held = {
                        entry,
                        contentId,
                        state: DevicePlaybackConcurrencyState.PLAYING,
                        playbackStarted: false,
                    }
                    reportPlayingAndGate(entry, contentId)
                })
            )

            add(
                player.paused$.onData((paused) => {
                    if (!paused) return
                    // End-of-track sets paused too; that isn't a user pause —
                    // the advance re-reports PLAYING for the next entry.
                    if (player.ended$.value) return
                    if (held?.state !== DevicePlaybackConcurrencyState.PLAYING) return
                    // A muted-autoplay-fallback bounce (play→pause→play) fires
                    // `pause` before the video ever produces audio; that isn't a
                    // user pause, so don't release the slot for it.
                    if (!held.playbackStarted) return
                    held.state = DevicePlaybackConcurrencyState.PAUSED
                    reportDeviceStreaming(
                        held.contentId,
                        player.contentTime$.value * 1000,
                        DevicePlaybackConcurrencyState.PAUSED
                    ).catch(errorHandler)
                })
            )

            add(
                player.playing$.onData((playing) => {
                    if (!playing || !held) return
                    // Mark the slot as having produced audio so a later genuine
                    // pause reports PAUSED (see paused$ handler).
                    const resuming = held.state === DevicePlaybackConcurrencyState.PAUSED
                    held.playbackStarted = true
                    // Only a genuine resume of the paused track re-acquires; the
                    // initial play after a track change is already covered by
                    // the PLAYING report in `current.onData`. Gate the resume the
                    // same way — another device may have claimed the slot while
                    // we were paused, in which case DMCS denies it and we prompt.
                    if (!resuming) return
                    held.state = DevicePlaybackConcurrencyState.PLAYING
                    reportPlayingAndGate(held.entry, held.contentId)
                })
            )
        })
        .catch(errorHandler)

    return dispose
}
